import React, { useEffect, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { processFieldRegionsAdmin, uploadFieldsAdmin } from "services";

import {
  addShapeProcessor,
  checkValidShapefileSize,
  checkValidShapefileType,
  getShapefileRegions,
  processGeometries,
  useCancelAsyncOnUnmount,
  useGetFarmDataSubmissionStatus,
  useUpdateFarmDataSubmissionAdmin,
} from "utils";

import { GET_FARM_DATA_SUBMISSION_STATUS } from "consts";

import { AdminSection, Button, Checkbox, Modal, Upload, Message as message } from "components";

export const FarmAdminTab = ({ farm, updateFarmDeal, updatingDeal, setShowModal, isTest, handleTestChecked }) => {
  const queryClient = useQueryClient();
  const [loadingFields, setLoadingFields] = useState(false);
  const { data: submissionStatusValue, isPending: loadingSubmissionStatusValue } = useGetFarmDataSubmissionStatus({
    farmId: farm?.id ?? "",
    isAdmin: true,
  });
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const { mutateAsync: updateFarmDataSubmissionAdmin, isPending: isPendingUpdateFarmDataSubmissionAdmin } = useUpdateFarmDataSubmissionAdmin();
  const signal = useCancelAsyncOnUnmount()();

  const uploadFields = async (geometries) => {
    const regionEnums = await getShapefileRegions({ signal });
    setLoadingFields(true);
    await uploadFieldsAdmin({
      geometries: geometries,
      region: Object.keys(regionEnums).find((key) => regionEnums[key] === farm.region),
      farmId: farm.id,
    })
      .then(() =>
        message({
          content: "The fields were successfully drawn by the gremlins.",
          icon: "gremlin",
        })
      )
      .finally(() => {
        setLoadingFields(false);
        processFieldRegionsAdmin({ farmId: farm.id });
      });
  };

  const onError = (error) => {
    message({
      content: `File processing failed with the error ${error}.`,
      icon: "gremlin",
    });
  };

  const processShapes = async (data) => {
    const processedGeometries = await processGeometries(data, onError);
    if (processedGeometries.length) {
      uploadFields(processedGeometries);
    }
  };

  const processShapeFile = (file) => {
    const reader = new FileReader();
    const fileType = file.name.split(".").pop();

    reader.onabort = () =>
      message({
        content: "File reading was aborted unexpectedly. Try again.",
        icon: "gremlin",
      });

    reader.onerror = () =>
      message({
        content: "File reading was failed unexpectedly. Try again.",
        icon: "gremlin",
      });

    reader.onloadend = async () => {
      const binaryStr = reader.result;

      if (fileType === "zip") {
        processShapes(binaryStr);
      } else {
        message({
          content: "You must upload a valid zip file with shp, prj and dbf files included.",
          icon: "gremlin",
        });
      }
    };
    if (fileType === "zip") {
      reader.readAsArrayBuffer(file);
    } else {
      message({
        content: "You must upload a valid zip file with shp, prj and dbf files included.",
        icon: "gremlin",
      });
    }
  };

  const fileUpload = async (file) => {
    setLoadingFields(true);
    const isValidType = await checkValidShapefileType(file);
    const isValidSize = await checkValidShapefileSize(file);

    if (!isValidType) {
      message({
        content: "Gremlins only eat zip files.",
        icon: "gremlin",
      });
    } else if (!isValidSize) {
      message({
        content: "Your file was too large for the gremlins.",
        icon: "gremlin",
      });
    } else {
      processShapeFile(file);
    }
  };

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: [GET_FARM_DATA_SUBMISSION_STATUS, farm?.id ?? ""] });
    addShapeProcessor();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isUndoSubmissonInValidDateRange = () => {
    const today = new Date();
    const currentMonth = today.getMonth(); // 0-11 (0 = January, 8 = September)
    // Valid range: September (8) to December (11)
    return currentMonth >= 8 && currentMonth <= 11;
  };

  const handleChangeDataSubmissionState = async () => {
    if (farm?.id) {
      const updateFarmDataSubmissionAdminResponse = await updateFarmDataSubmissionAdmin({ farmId: farm.id, submissionState: false });
      if (updateFarmDataSubmissionAdminResponse.success) message({ icon: "tick", message: updateFarmDataSubmissionAdminResponse.message });
      else message({ icon: "error", message: updateFarmDataSubmissionAdminResponse.message });
    } else {
      message({ icon: "error", message: "There is no farm" });
    }
    setShowConfirmationModal(false);
  };

  return (
    farm && (
      <>
        <AdminSection
          headline="Test farm"
          description={
            isTest ? "This farm is marked as a test farm. You can update it here." : "This farm is not marked as a test farm. You can update it here."
          }
          extra={
            <Checkbox checked={isTest} onChange={(e) => handleTestChecked(e.target.checked)}>
              Test farm
            </Checkbox>
          }
        />
        <AdminSection
          headline="Pipedrive"
          description={
            farm.dealId
              ? "This farm has a deal associated with it. You can update the Pipedrive deal data from here."
              : "This farm does not have Pipedrive deal associated with it. You can link a deal here."
          }
          extra={
            farm.dealId ? (
              <Button type="primary" onClick={updateFarmDeal} size="small" loading={updatingDeal}>
                Update deal
              </Button>
            ) : (
              <Button type="primary" onClick={() => setShowModal(true)} size="small" loading={updatingDeal}>
                Link deal
              </Button>
            )
          }
        />
        <AdminSection
          headline="Shapefile upload"
          description={
            <>
              Upload a shapefile for this farm. <b>Warning: uploading a shape file will modify this farmer's fields. It cannot be undone.</b>
            </>
          }
          extra={
            <Upload action="" beforeUpload={(v) => fileUpload(v)} name="file" showUploadList={false} className="auto-height auto-width">
              <Button type="primary" size="small" disabled={loadingFields} loading={loadingFields}>
                Upload file
              </Button>
            </Upload>
          }
        />
        <AdminSection
          headline="Undo Submission"
          description={
            <>
              Only enabled if the farmer has already submitted data and between 1 September and 31 December.
              <b>Warning: It cannot be undone.</b>
            </>
          }
          extra={
            <Button
              type="primary"
              size="small"
              disabled={!submissionStatusValue?.isSubmitted || !isUndoSubmissonInValidDateRange() || submissionStatusValue?.payoutApproved}
              loading={loadingSubmissionStatusValue || isPendingUpdateFarmDataSubmissionAdmin}
              onClick={() => setShowConfirmationModal(true)}
            >
              Undo Submission
            </Button>
          }
        />
        <Modal
          cancelText={"Cancel"}
          className="confirm-baseline-drawer"
          dataTestId="confirm-undo-submission-modal"
          description={
            "Are you sure you want to revert this submission?\n" +
            "This action will set the user’s status back to 'not submitted' and reopen their data entry.\n" +
            "Only the user can resubmit — the admin cannot submit for them.\n" +
            "This action cannot be undone.”"
          }
          onCancel={() => setShowConfirmationModal(false)}
          onOk={handleChangeDataSubmissionState}
          okText={"Undo Submission"}
          showModal={showConfirmationModal}
          textColour="deep-black-text"
          title={"Undo Submission"}
          loading={isPendingUpdateFarmDataSubmissionAdmin}
        />
      </>
    )
  );
};
